// News preview sheet (slides in from the right, bottom sheet on phones).
import { useT } from '@/i18n';
import { Component, createContext, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { newsCtx } from '@/features/ai/contexts';
import { AskOrca } from '@/features/ai/OrcaAnswer';
import { useBodyClass } from '@/app/providers/bodyClass';
import { Icon } from '@/components/ui/Icon';
import { Importance } from '@/components/ui/Indicators';
import { usePortfolio } from '@/hooks/derived';
import { useMarket, useNewsPage } from '@/hooks/queries';
import { cx, nf, pct } from '@/lib/format';
import { CAT_ICON, CAT_SHORT, NewsImage } from './NewsCard';
import { useTime } from '@/hooks/useTime';
import { useAskContext } from '@/features/ai/useAskContext';
import { closeAbove, closeTopLayer, topLayer, useLayer } from '@/lib/overlay';
import { useQueryClient } from '@tanstack/react-query';
import { MOCK_DATA } from '@/lib/api/mode';
import { realApi } from '@/lib/api/real';
import type { NewsPage } from '@/types';

type SheetCtx = { openNews: (id: number, list?: number[]) => void; closeNews: () => void; hideNews: () => void };
const Ctx = createContext<SheetCtx>({ openNews: () => {}, closeNews: () => {}, hideNews: () => {} });
export const useNewsSheet = () => useContext(Ctx);

export function NewsSheetProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ id: number; list: number[] } | null>(null);
  const openNews = useCallback((id: number, list?: number[]) => {
    setState({ id, list: list && list.includes(id) ? list : [id] });
  }, []);
  const [params, setParams] = useSearchParams();
  const hasId = params.has('id');
  // closing the preview also drops ?id= so the address is the plain page again
  const closeNews = useCallback(() => {
    setState(null);
    if (hasId) setParams((p) => { const q = new URLSearchParams(p); q.delete('id'); return q; }, { replace: true });
  }, [hasId, setParams]);
  /** page change: just hide, the new address stays as it is */
  const hideNews = useCallback(() => setState(null), []);
  const value = useMemo(() => ({ openNews, closeNews, hideNews }), [openNews, closeNews, hideNews]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <SilentBoundary><Suspense fallback={null}>
        <NewsPreview state={state} onNavigate={(id) => setState((s) => (s ? { ...s, id } : s))} onClose={closeNews} />
      </Suspense></SilentBoundary>
    </Ctx.Provider>
  );
}

/** The preview lives outside the page boundary: if its data fails, it just doesn't render. */
class SilentBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() { return this.state.failed ? null : this.props.children; }
}

function NewsPreview({ state, onNavigate, onClose }: { state: { id: number; list: number[] } | null; onNavigate: (id: number) => void; onClose: () => void }) {
  const tf = useTime();
  const qc = useQueryClient();
  const { watchlist } = useMarket();
  const pf = usePortfolio();
  const [leaving, setLeaving] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const open = !!state;
  useBodyClass('sheet-open', open);
  // one modal stack: Esc / the scrim close the top layer (Orca opened from here closes first)
  const top = useLayer('news', open, onClose);
  const asideRef = useRef<HTMLElement>(null);
  useEffect(() => { if (open) asideRef.current?.focus({ preventScroll: true }); }, [open]);
  // keep the last item rendered while the sheet slides out
  const [shownId, setShownId] = useState<number | null>(null);
  useEffect(() => { if (state) setShownId(state.id); }, [state]);
  useEffect(() => { bodyRef.current?.scrollTo(0, 0); setLeaving(false); }, [shownId]);

  const list = state?.list ?? [];
  const pos = state ? list.indexOf(state.id) : -1;
  const prev = () => { if (pos > 0) onNavigate(list[pos - 1]); };
  const next = () => { if (pos >= 0 && pos < list.length - 1) onNavigate(list[pos + 1]); };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (topLayer() !== 'news') return;
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // the article is already on screen in a loaded page (News, Overview, Signals, Portfolio);
  // a link that opens one which is not (/news?id=…) asks the server for that article only
  const cached = shownId === null ? undefined : qc.getQueriesData<NewsPage>({ queryKey: ['news-page'] })
    .flatMap(([, d]) => (d ? [...d.items, ...d.summaryTop] : [])).find((x) => x.id === shownId);
  const one = useNewsPage({ scope: 'week', coin: 'all', cat: 'all', page: 1, perPage: 1, ids: shownId === null ? [] : [shownId] },
    open && shownId !== null && !cached).data;
  const n = cached ?? one?.items.find((x) => x.id === shownId);
  // related: the newest articles about the same coin — a small page, only while the sheet is open
  const rel = useNewsPage({ scope: 'week', coin: n?.tags[0] ?? 'all', cat: 'all', page: 1, perPage: 4 }, open && !!n?.tags.length).data;
  const { t } = useT('news');
  // opening an article marks it read on the Web only (news_reads); a Telegram delivery is never implied
  useEffect(() => {
    if (MOCK_DATA || !open || !n || !n.isNew) return;
    const id = n.id;
    realApi.markNewsRead([id]).then(() => {
      // the News page's unread counter and «new» marks come from the server pages
      void qc.invalidateQueries({ queryKey: ['news-page'] });
    }, () => { /* a read mark is best effort */ });
  }, [open, n?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // the open news is what the Orca bar talks about (its questions replace a separate News Impact)
  useAskContext(open && n ? newsCtx(n, tf) : null, 3);
  let content: ReactNode = null;
  if (n) {
    const mine = n.tags.filter((t) => watchlist.some((c) => c.symbol === t));
    const related = (rel?.items ?? []).filter((x) => x.id !== n.id).slice(0, 3);
    content = (
      <>
        <div className={cx('sh-img', n.category)}>
          <NewsImage n={n} className="sh-pic" label />
          <div className="sh-img-top"><span className="sh-cat"><Icon name={CAT_ICON[n.category]} />{CAT_SHORT[n.category]}</span>{n.isNew && <em className="nt-new">{t('new')}</em>}</div>
          <div className="sh-img-b"><time dateTime={n.at}>{tf.dayDiff(n.at) >= -1 ? tf.dayLabel(n.at) : `${tf.dayShort(n.at)}, ${tf.dateLong(n.at)}`} · {tf.time(n.at)} {tf.zone}</time><div className="nt-coins">{n.tags.map((t) => <b key={t}>{t}</b>)}</div></div>
        </div>
        <div className="sh-c">
          <h2 className="sh-t">{n.title}</h2>
          <p className="sh-dek">{n.dek}</p>
          {n.body && <div className="sh-sec"><span className="k">{t('sheet.brief')}</span><p>{n.body}</p></div>}
          {n.why && <div className="sh-sec why"><span className="k">{t('sheet.why')}</span><p>{n.why}</p></div>}
          <div className="sh-sec you"><span className="k">{t('sheet.forYou')}</span><p>
            {mine.length ? mine.map((tag, i) => {
              const h = pf.rows.find((r) => r.symbol === tag);
              const c = watchlist.find((w) => w.symbol === tag)!;
              return <span key={tag}>{i > 0 && <br />}<b>{tag}</b>{h ? ` — ${t('sheet.share', { share: nf(h.share, 1), ch: pct(c.change24h) })}` : ` ${t('sheet.inCoins', { ch: pct(c.change24h) })}`}</span>;
            }) : t('sheet.notYours')}
          </p></div>
          {(n.imp || n.sources !== undefined || n.telegramIncludedAt) && (
            <div className="sh-meta">
              {n.imp ? <span><Importance n={n.imp} /> {t('sheet.importance', { n: n.imp })}</span> : null}
              {n.sources !== undefined && <span>{t('common:unit.source', { count: n.sources })}</span>}
              {!!n.dupes && <span>{t('dupesHidden', { n: n.dupes })}</span>}
              {n.telegramIncludedAt && <span>{t('sheet.inTelegram', { when: `${tf.stamp(n.telegramIncludedAt)} ${tf.zone}` })}</span>}
            </div>
          )}
          <div className="sh-src">
            <div className="sh-src-t">
              <span className="k">{t('sheet.source')}</span>
              <b>{n.source.name}<small>{n.source.url.replace(/^https?:\/\/(www\.)?/, '')}</small></b>
              {n.alsoIn.length > 0 && <small className="sh-also">{t('sheet.alsoIn', { list: n.alsoIn.join(', ') })}</small>}
            </div>
            {!leaving
              ? <button type="button" className="sh-open" id="shOrig" onClick={() => setLeaving(true)}>{t('sheet.openSource')}<span aria-hidden="true">↗</span></button>
              : (
                <div className="sh-leave" role="dialog" aria-label={t('sheet.leaveAria')}>
                  <p>{t('sheet.leave1')} <b>{n.source.name}</b> {t('sheet.leave2')}</p>
                  <div><a className="sh-open on" href={n.source.url} target="_blank" rel="noreferrer" onClick={() => setLeaving(false)}>{t('sheet.go')}<span aria-hidden="true">↗</span></a><button type="button" className="stc-link" onClick={() => setLeaving(false)}>{t('common:action.cancel')}</button></div>
                </div>
              )}
          </div>
          <div className="sh-actions">
            <AskOrca ctx={newsCtx(n, tf)} />
            <span className="sh-hint">{t('sheet.askHint')}</span>
          </div>
          {related.length > 0 && (
            <div className="sh-rel"><span className="k">{t('sheet.related')}</span>
              {related.map((r) => (
                <button key={r.id} type="button" onClick={() => onNavigate(r.id)}>
                  <NewsImage n={r} className="sh-rel-pic" />
                  <span><b>{r.title}</b><small>{tf.stamp(r.at)}</small></span>
                </button>
              ))}
            </div>
          )}
        </div>
      </>
    );
  }
  return (
    <>
      <div className="sheet-scrim" onClick={closeTopLayer} />
      <aside className="sheet" role="dialog" aria-modal={top} aria-label={t('sheet.aria')} inert={!open} tabIndex={-1} ref={asideRef}
        onPointerDownCapture={() => closeAbove('news')}>
        <header className="sh-bar">
          <button className="pg-arrow sm" type="button" aria-label={t('sheet.prev')} disabled={pos <= 0} onClick={prev}><svg viewBox="0 0 24 24"><path d="m15 6-6 6 6 6" /></svg></button>
          <button className="pg-arrow sm" type="button" aria-label={t('sheet.next')} disabled={pos < 0 || pos >= list.length - 1} onClick={next}><svg viewBox="0 0 24 24"><path d="m9 6 6 6-6 6" /></svg></button>
          <span className="sh-count">{list.length > 1 ? t('sheet.pos', { n: pos + 1, total: list.length }) : ''}</span>
          <button className="dr-x" type="button" aria-label={t('common:a11y.close')} onClick={onClose}><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18" /></svg></button>
        </header>
        <div className="sheet-body" ref={bodyRef}>{content}</div>
      </aside>
    </>
  );
}
